import {
  Box,
  Button,
  Container,
  Stack,
  Text,
} from '@spaceship-fspl/components';
import { PostsOrPages, Tag } from '@tryghost/content-api';
import {
  ArticleCard,
  BlogTitle,
  BlogVerticalMarginContainer,
  getBlogPageLayoutStaticProps,
  getMetadataPropsFromGhostPostPageTagOrAuthor,
  Metadata,
  TiledArticleCards,
  withBlogPageLayout,
} from 'components';
import { getPosts, getTag, getTags } from 'data/ghost/api-cached';
import { usePosts } from 'data/ghost/hooks';
import { GetStaticPaths, NextPage } from 'next';
import { ParsedUrlQuery } from 'querystring';
import React from 'react';

const INITIAL_POST_COUNT = 9;

interface TagPageProps {
  tag: Tag;
  posts: PostsOrPages;
}

const TagPage: NextPage<TagPageProps> = ({ tag, posts: initialPosts }) => {
  const metadata = getMetadataPropsFromGhostPostPageTagOrAuthor(tag);
  const {
    data: allPosts,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = usePosts({
    initialData: initialPosts,
    params: { tag: tag.slug, limit: INITIAL_POST_COUNT },
  });

  return (
    <BlogVerticalMarginContainer>
      <Container>
        <Metadata {...metadata} />

        <Stack spaceY={{ xs: 'xl', lg: 'xxl' }} alignX="center">
          <Stack spaceY="md">
            <BlogTitle>{tag.name}</BlogTitle>

            <Text align="center" variant={1} color="neutral.080">
              {tag.description}
            </Text>
          </Stack>

          <TiledArticleCards>
            {allPosts?.pages.flatMap((posts) =>
              posts.map(
                ({
                  title,
                  slug,
                  feature_image,
                  excerpt,
                  primary_author,
                  reading_time,
                  primary_tag,
                }) => (
                  <ArticleCard
                    key={slug}
                    slug={slug}
                    title={title}
                    image={feature_image}
                    tag={primary_tag}
                    description={excerpt}
                    author={{
                      ...primary_author,
                      profileImage: primary_author?.profile_image,
                    }}
                    readingTime={reading_time}
                  />
                ),
              ),
            )}
          </TiledArticleCards>

          {hasNextPage && (
            <Box display="flex" justifyContent="center">
              <Button
                variant="tertiary"
                size="lg"
                isLoading={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                Load more
              </Button>
            </Box>
          )}
        </Stack>
      </Container>
    </BlogVerticalMarginContainer>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const tags = await getTags({ limit: 'all' });

  return {
    paths: tags.map((tag) => ({
      params: {
        slug: tag.slug,
      },
    })),
    fallback: false,
  };
};

interface Context extends ParsedUrlQuery {
  slug: string;
}

export const getStaticProps = getBlogPageLayoutStaticProps<
  TagPageProps,
  Context
>(async (context) => {
  const slug = context?.params?.slug ?? '';
  return {
    props: {
      tag: await getTag(slug),
      posts: await getPosts({ tag: slug, limit: INITIAL_POST_COUNT }),
    },
  };
});

export default withBlogPageLayout()(TagPage);
